import { useEffect, useMemo, useState } from 'react';
import { formatTimer } from '../hooks/useCountdown';
import type { LotState } from '../hooks/useLotState';

type Props = {
  state: LotState;
  balanceBids?: number;
  timerEndsAt?: number;
  bidsCount?: number;
  // True once this client knows the free first-bid slot is gone (lost the race
  // or not eligible). Combined with bidsCount===0 to decide the FREE BID label.
  freeSlotTaken?: boolean;
  onBid?: (freeIntent: boolean) => void;
  onRemind?: () => void;
  buyNowVisible?: boolean;
  buyNowPrice?: number;
  viewerBidsCount?: number;
  marketPrice?: number;
  viewerIsWinner?: boolean;
  onBuyNow?: () => void;
  onTopUp?: () => void;
  onClaim?: () => void;
};

// `bumpKey` lets a caller force the window to reset even when `timerEndsAt`
// itself didn't move. Use case: a bid whose extension is shorter than the
// current remaining time leaves `timerEndsAt` unchanged on the server
// (Math.max keeps the larger value), but without a reset the on-button
// progress bar appears frozen on the bid — looks like the button didn't
// react. Pass `bidsCount` so the window restarts from "what's left now"
// every time a bid lands.
function useTimerWindow(timerEndsAt: number | undefined, bumpKey: number) {
  const [, setTick] = useState(0);

  // bumpKey participates so a no-op extension still restarts the window.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bumpKey is the reset trigger
  const windowMeta = useMemo(() => {
    if (timerEndsAt == null) return null;
    const start = Date.now();
    const durationMs = timerEndsAt - start;
    if (durationMs <= 0) return null;
    return { start, durationMs };
  }, [timerEndsAt, bumpKey]);

  useEffect(() => {
    if (timerEndsAt == null) return;
    let raf = 0;
    const loop = () => {
      setTick((t) => (t + 1) % 1_000_000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [timerEndsAt]);

  const remainingMs = timerEndsAt ? Math.max(0, timerEndsAt - Date.now()) : 0;
  const progress =
    windowMeta && windowMeta.durationMs > 0
      ? Math.min(1, Math.max(0, remainingMs / windowMeta.durationMs))
      : 0;
  return { remainingMs, progress };
}

export function LotMainButton({
  state,
  balanceBids = 0,
  timerEndsAt,
  bidsCount = 0,
  freeSlotTaken = false,
  onBid,
  onRemind,
  buyNowVisible = false,
  buyNowPrice = 0,
  viewerBidsCount = 0,
  marketPrice,
  viewerIsWinner = false,
  onBuyNow,
  onTopUp,
  onClaim,
}: Props) {
  const { remainingMs, progress } = useTimerWindow(
    state === 'live' ? timerEndsAt : undefined,
    bidsCount,
  );
  const remainingSec = Math.ceil(remainingMs / 1000);
  const critical = state === 'live' && remainingSec > 0 && remainingSec <= 3;
  const urgent = state === 'live' && remainingSec > 0 && remainingSec <= 7;

  // The free first-bid is available while the lot has no bids and this client
  // hasn't lost the race. It's offered to everyone — including zero-balance
  // viewers — so it must outrank the out-of-bids pivot below.
  const freeMode = state === 'live' && bidsCount === 0 && !freeSlotTaken;

  // Out of bids on a live lot: BID is unusable, so the footer pivots to the next
  // best action. If the viewer already bid on this lot, BIN is the primary CTA
  // (their spent bids convert into a discount per penny-format §2.1) and Top up
  // is the secondary link. If they never bid here, BIN doesn't apply — Top up
  // becomes the primary CTA so they can return to bidding. Suppressed in
  // freeMode: a zero-balance viewer can still take the free bid.
  const outOfBidsLive = state === 'live' && balanceBids <= 0 && !freeMode;
  const winnerOnClosed = state === 'closed' && viewerIsWinner;
  const buyNowAsFloatingCta = buyNowVisible && (state === 'closed' || outOfBidsLive);
  const topUpOnlyCta = outOfBidsLive && !buyNowVisible;

  if (state === 'closed' && !buyNowAsFloatingCta && !winnerOnClosed) return null;

  if (winnerOnClosed) {
    return (
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 px-3 pb-3 pt-2 bg-gradient-to-t from-bg via-bg/95 to-transparent">
        <button
          type="button"
          onClick={onClaim}
          disabled={!onClaim}
          className="relative w-full h-14 overflow-hidden text-bg font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform disabled:opacity-60 disabled:active:scale-100"
          style={{
            backgroundColor: 'var(--color-success)',
            boxShadow: '0 0 26px rgba(0,255,148,0.45), inset 0 0 0 1px rgba(255,255,255,0.14)',
          }}
        >
          <span
            className="relative flex items-center justify-center gap-3 text-[16px]"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}
          >
            <span aria-hidden>★</span>
            <span>Claim your skin</span>
          </span>
        </button>
        <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
          you won this lot · trade URL setup coming soon
        </p>
      </div>
    );
  }

  const buyNowSavings =
    marketPrice != null && marketPrice > buyNowPrice ? marketPrice - buyNowPrice : 0;

  const buyNowFloatingButton = (
    <button
      type="button"
      onClick={onBuyNow}
      disabled={!onBuyNow}
      className="relative w-full h-14 overflow-hidden text-bg font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform disabled:opacity-60 disabled:active:scale-100"
      style={{
        backgroundColor: 'var(--color-primary)',
        boxShadow: '0 0 24px rgba(0,229,255,0.42), inset 0 0 0 1px rgba(255,255,255,0.12)',
      }}
    >
      <span
        className="relative flex items-center justify-between px-5 text-[16px]"
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}
      >
        <span className="flex items-baseline gap-2">
          <span>BUY NOW</span>
          {buyNowSavings > 0 && (
            <span className="font-mono text-[12px] opacity-90 tracking-wider">
              −${buyNowSavings.toFixed(2)}
            </span>
          )}
        </span>
        <span className="font-mono text-[15px] tabular-nums">${buyNowPrice.toFixed(2)}</span>
      </span>
    </button>
  );

  if (buyNowAsFloatingCta) {
    return (
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 px-3 pb-3 pt-2 bg-gradient-to-t from-bg via-bg/95 to-transparent">
        {buyNowFloatingButton}
        {outOfBidsLive && (
          // Ghost secondary in the Lot-chat language (border-primary/40 +
          // bg-primary/5, mono caps) so the pair reads as primary + supporting,
          // not two competing CTAs.
          <button
            type="button"
            onClick={onTopUp}
            disabled={!onTopUp}
            className="w-full h-11 mt-2 border border-primary/40 bg-primary/5 text-primary flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-primary/10 hover:border-primary/70 active:scale-[0.99] transition-all disabled:opacity-60 disabled:active:scale-100"
          >
            <span aria-hidden>＋</span>
            <span>Top up bids</span>
          </button>
        )}
        <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
          {outOfBidsLive
            ? `out of bids · your ${viewerBidsCount} bid${viewerBidsCount === 1 ? '' : 's'} count as discount`
            : `your ${viewerBidsCount} bid${viewerBidsCount === 1 ? '' : 's'} count as discount`}
        </p>
      </div>
    );
  }

  if (topUpOnlyCta) {
    return (
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 px-3 pb-3 pt-2 bg-gradient-to-t from-bg via-bg/95 to-transparent">
        <button
          type="button"
          onClick={onTopUp}
          disabled={!onTopUp}
          className="relative w-full h-14 overflow-hidden text-bg font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform disabled:opacity-60 disabled:active:scale-100"
          style={{
            backgroundColor: 'var(--color-primary)',
            boxShadow: '0 0 24px rgba(0,229,255,0.42), inset 0 0 0 1px rgba(255,255,255,0.12)',
          }}
        >
          <span
            className="relative flex items-center justify-center px-5 text-[16px]"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}
          >
            Top up bids
          </span>
        </button>
        <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
          out of bids — buy a pack to keep bidding
        </p>
      </div>
    );
  }

  const fillColor = critical
    ? 'var(--color-danger)'
    : urgent
      ? 'var(--color-warning)'
      : 'var(--color-accent)';
  const glowRgba = critical
    ? 'rgba(255,77,77,0.55)'
    : urgent
      ? 'rgba(255,217,61,0.50)'
      : 'rgba(255,61,167,0.45)';

  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 px-3 pb-3 pt-2 bg-gradient-to-t from-bg via-bg/95 to-transparent">
      {state === 'live' && (
        <>
          <button
            type="button"
            onClick={() => onBid?.(freeMode)}
            disabled={!onBid}
            className={`relative w-full h-14 group overflow-hidden text-bg font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform disabled:opacity-60 disabled:active:scale-100 ${critical ? 'anim-pulse-danger' : ''}`}
            style={{
              backgroundColor: 'var(--color-bg-elev)',
              boxShadow: `0 0 26px ${glowRgba}, inset 0 0 0 1px rgba(255,255,255,0.10)`,
            }}
          >
            <span
              aria-hidden
              className="absolute top-0 bottom-0 left-0"
              style={{
                width: `${progress * 100}%`,
                background: fillColor,
                boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.14)`,
              }}
            />
            <span
              className="absolute inset-0 anim-sweep pointer-events-none"
              style={{
                width: '40%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent)',
                mixBlendMode: 'overlay',
              }}
            />
            <span
              className="relative flex items-center justify-between px-5 text-[16px] text-white"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
            >
              {freeMode ? (
                <span>FREE BID</span>
              ) : (
                <span className="flex items-baseline gap-2">
                  <span>BID</span>
                  <span className="font-mono tabular-nums opacity-90">· {balanceBids}</span>
                </span>
              )}
              <span className="font-mono text-[13px] tabular-nums opacity-95">
                {timerEndsAt ? formatTimer(remainingSec) : '+10s'}
              </span>
            </span>
          </button>
          {freeMode ? (
            <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
              first bid is free — claim the slot before anyone else
            </p>
          ) : (
            viewerBidsCount > 0 && (
              <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
                spent {viewerBidsCount} bid{viewerBidsCount === 1 ? '' : 's'} on this lot
              </p>
            )
          )}
        </>
      )}

      {state === 'scheduled' && (
        <>
          <button
            type="button"
            onClick={onRemind}
            disabled={!onRemind}
            className="relative w-full h-14 group overflow-hidden border-2 border-primary text-primary font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform bg-bg/40 backdrop-blur-sm disabled:opacity-60 disabled:active:scale-100"
            style={{ boxShadow: '0 0 20px rgba(0,229,255,0.25)' }}
          >
            <span className="relative flex items-center justify-center gap-2 text-[15px]">
              <span>◉</span>
              <span>Remind me</span>
            </span>
          </button>
          <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
            first bid locks your slot — be early.
          </p>
        </>
      )}
    </div>
  );
}
